import { createSelector } from '@reduxjs/toolkit';
import { fetchBidLimitBalanceIfNeeded } from './bidLimit';
import { fetchLiveCatalogStatusIfNeeded } from './liveCatalogStatus';
import { getAuthToken, getBidderId } from './user';
import { getCatalog } from './catalog/catalog.selectors';
import { getCurrentUrl } from './route';
import { getDeployment } from './config';
import { getPlacedLiveBidAnalytics } from './analytics';
import { GlobalState } from '../rootReducer';
import {
    LIVE_BID_ACCEPTED,
    LIVE_LOT_CLOSED,
    LIVE_LOT_PASSED,
    LIVE_LOT_SKIPPED,
    LIVE_LOT_SOLD,
    LIVE_NEXT_LOT_LOADED,
    LOAD_LIVE_CATALOG_STATUS_SUCCESS,
} from './actions';
import { LOCATION_CHANGE } from 'connected-react-router';
import { postPlaceLiveBid } from '@/redux/api/bid';

/* Action Types */
export const LIVE_BID_FAIL = 'la/ui/liveBid/FAIL';
export const LIVE_BID_REQUEST = 'la/ui/liveBid/REQUEST';
export const LIVE_BID_SUCCESS = 'la/ui/liveBid/SUCCESS';

interface DefaultState {
    readonly bidderHasHighBid: boolean;
    readonly error: boolean;
    readonly submitted: boolean;
    readonly success: boolean;
}

// reducer
export const DEFAULT_STATE: DefaultState = {
    bidderHasHighBid: false,
    error: false,
    submitted: false,
    success: false,
};

export type State = typeof DEFAULT_STATE;

export default function reducer(state: State = DEFAULT_STATE, action: any = {}) {
    switch (action.type) {
        case LIVE_BID_REQUEST:
            return {
                ...state,
                submitted: true,
                success: false,
            };
        case LIVE_BID_ACCEPTED:
            return {
                ...state,
                bidderHasHighBid: action.payload.myBid,
                submitted: false,
                success: true,
            };
        case LIVE_LOT_CLOSED:
        case LIVE_LOT_PASSED:
        case LIVE_LOT_SKIPPED:
        case LIVE_LOT_SOLD:
            return {
                ...state,
                bidderHasHighBid: false,
                submitted: false,
                success: true,
            };
        case LIVE_BID_FAIL:
            return {
                ...state,
                error: true,
                submitted: false,
                success: false,
            };
        case LIVE_NEXT_LOT_LOADED:
            return {
                ...state,
                bidderHasHighBid: false,
                submitted: false,
            };
        case LOAD_LIVE_CATALOG_STATUS_SUCCESS:
            const currentHighBidderId = action.payload?.currentItem?.leadingBidder;
            return {
                ...state,
                bidderHasHighBid: action.meta.bidderId === currentHighBidderId,
            };
        case LOCATION_CHANGE:
            return DEFAULT_STATE;
        default:
            return state;
    }
}

/* SELECTORS */
const stateSelector = (state: GlobalState) => state.liveBid;
export const getUiLiveBid = createSelector(stateSelector, (state) => state);

export const getBidderHasHighBid = createSelector(stateSelector, (state) => state.bidderHasHighBid);
export const getBidderSubmittedBid = createSelector(stateSelector, (state) => state.submitted);

const getIsMobileHack = createSelector(
    (state: any) => state.browser,
    (state: any) => state.mobile
);

type BidSource = '' | 'MobileItem' | 'DesktopItem' | 'MobileWeb' | 'DesktopWeb';
export const getBidSource = createSelector(
    [getIsMobileHack, getCurrentUrl],
    (mobile: string, currentUrl: { pathname: string }): BidSource => {
        const { pathname } = currentUrl;

        let bidSource: BidSource = '';
        if (pathname.includes('item')) {
            if (mobile) {
                bidSource = 'MobileItem';
            } else {
                bidSource = 'DesktopItem';
            }
        } else {
            if (mobile) {
                bidSource = 'MobileWeb';
            } else {
                bidSource = 'DesktopWeb';
            }
        }
        return bidSource;
    }
);

/* ACTIONS CREATORS */
export const submitLiveBid =
    (actionTime: number, catalogId: number, currentAskPrice: number, currency: string, itemId: number) =>
    async (dispatch: Function, getState: Function) => {
        try {
            const state = getState();
            const created = new Date(actionTime).toISOString();
            const authToken = getAuthToken(state);
            const deployment = getDeployment(state);
            const { sellerId } = getCatalog(state, catalogId);
            const bidderId = getBidderId(state);
            const source = getBidSource(state);

            await dispatch(fetchLiveCatalogStatusIfNeeded(catalogId, sellerId));

            dispatch({
                payload: {
                    amount: currentAskPrice,
                    catalogId,
                    created,
                    currency,
                },
                type: LIVE_BID_REQUEST,
            });

            if (!authToken || !itemId || !catalogId || !currentAskPrice || bidderId < 1) {
                throw new Error(`Failed to submit bid, missing critical information`);
            }

            await postPlaceLiveBid({
                authToken,
                bidAmount: currentAskPrice,
                catalogId,
                deployment,
                itemId,
                sellerId,
                source,
            });

            // currently this is used only for the analytics
            const analytics = getPlacedLiveBidAnalytics(state, itemId, currentAskPrice);
            dispatch({
                meta: { analytics },
                payload: {
                    actionTime,
                    amount: currentAskPrice,
                    catalogId,
                    created,
                    currency,
                },
                type: LIVE_BID_SUCCESS,
            });
            // Fetch the bid limit balance every time we place a bid. (maybe this can get returned from the bid api someday)
            return dispatch(fetchBidLimitBalanceIfNeeded({ catalogId, itemId }));
        } catch (error) {
            dispatch({
                error: true,
                payload: error,
                type: LIVE_BID_FAIL,
            });
        }
    };
